import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import React from 'react';
import PropTypes from 'prop-types';
import invariant from 'invariant';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import { pickHTMLProps } from 'pick-react-known-prop';
import useCalculateProductPriceHanlder from '@sprinx/react-pricing/useCalculateProductPriceHandler';
import ProductPriceStandard from './ProductPriceStandard';
var useStyles = makeStyles(function () {
  return (
    /* theme */
    {
      root: {},
      fullWidth: {
        width: '100%'
      }
    }
  );
}, {
  name: 'SxComProductPrice'
});
var ProductPrice = /*#__PURE__*/React.forwardRef(function ProductPrice(_ref, ref) {
  var children = _ref.children,
      pClasses = _ref.classes,
      className = _ref.className,
      currency = _ref.currency,
      decimalSignificance = _ref.decimalSignificance,
      pDiscountAbsolute = _ref.discountAbsolute,
      pDiscountPercent = _ref.discountPercent,
      fullWidth = _ref.fullWidth,
      pNoTaxPrices = _ref.noTaxPrices,
      pPrice = _ref.price,
      pPriceOriginal = _ref.priceOriginal,
      pPriceOriginalWithTax = _ref.priceOriginalWithTax,
      pPriceWithTax = _ref.priceWithTax,
      product = _ref.product,
      StandardComponentProsp = _ref.StandardComponentProsp,
      pTaxInfo = _ref.taxInfo,
      rest = _objectWithoutProperties(_ref, ["children", "classes", "className", "currency", "decimalSignificance", "discountAbsolute", "discountPercent", "fullWidth", "noTaxPrices", "price", "priceOriginal", "priceOriginalWithTax", "priceWithTax", "product", "StandardComponentProsp", "taxInfo"]);

  var classes = useStyles({
    classes: pClasses
  });
  invariant(product || pPrice && pPriceWithTax, 'ProductPrice requires one of `product` or `price + priceWithTax` props to be set.');
  var calculateProductPrice = useCalculateProductPriceHanlder(null);

  var _React$useState = React.useState({
    discountAbsolute: undefined,
    discountPercent: undefined,
    taxInfo: undefined,
    noTaxPrices: false,
    price: undefined,
    priceWithTax: undefined,
    priceOriginal: undefined,
    priceOriginalWithTax: undefined
  }),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      _React$useState2$ = _React$useState2[0],
      discountAbsolute = _React$useState2$.discountAbsolute,
      discountPercent = _React$useState2$.discountPercent,
      noTaxPrices = _React$useState2$.noTaxPrices,
      price = _React$useState2$.price,
      priceOriginal = _React$useState2$.priceOriginal,
      priceOriginalWithTax = _React$useState2$.priceOriginalWithTax,
      priceWithTax = _React$useState2$.priceWithTax,
      taxInfo = _React$useState2$.taxInfo,
      setPrices = _React$useState2[1];

  React.useEffect(function () {
    if (pPrice && pPriceWithTax) {
      setPrices({
        discountAbsolute: pDiscountAbsolute,
        discountPercent: pDiscountPercent,
        noTaxPrices: pNoTaxPrices,
        price: pPrice,
        priceOriginal: pPriceOriginal,
        priceOriginalWithTax: pPriceOriginalWithTax,
        priceWithTax: pPriceWithTax,
        taxInfo: pTaxInfo
      });
    } else if (product) {
      calculateProductPrice(product).then(setPrices);
    }
  }, [calculateProductPrice, pPrice, pPriceWithTax, pPriceOriginal, pPriceOriginalWithTax, product, pDiscountAbsolute, pDiscountPercent, pNoTaxPrices, pTaxInfo]);
  return /*#__PURE__*/React.createElement("div", _extends({}, pickHTMLProps(rest), {
    className: clsx(classes.root, className, fullWidth && classes.fullWidth),
    ref: ref
  }), children ? children({
    currency: currency,
    isDiscount: !!discountAbsolute,
    noTaxPrices: noTaxPrices,
    price: price,
    priceWithTax: priceWithTax,
    priceOriginal: priceOriginal,
    priceOriginalWithTax: priceOriginalWithTax,
    discountAbsolute: discountAbsolute,
    discountPercent: discountPercent,
    taxInfo: taxInfo
  }) : /*#__PURE__*/React.createElement(ProductPriceStandard, _extends({}, StandardComponentProsp, {
    currency: currency,
    isDiscount: !!discountAbsolute,
    price: price,
    priceWithTax: priceWithTax,
    priceOriginal: priceOriginal,
    priceOriginalWithTax: priceOriginalWithTax,
    discountAbsolute: discountAbsolute,
    discountPercent: discountPercent,
    noTaxPrices: noTaxPrices
  })));
});
ProductPrice.propTypes = {
  /**
   * Signature:
   * ```js
   * (({
   *   currency: string,
   *   isDiscount: boolean,
   *   noTaxPrices: boolean,
   *   price: number,
   *   priceWithTax: number,
   *   priceOriginal: number | undefined, // `undefined` if there is no discount
   *   priceOriginalWithTax: number | undefined, // `undefined` if there is no discount
   *   discountAbsolute:: number | undefined, // `undefined` if there is no discount
   *   discountPercent: number | undefined, // `undefined` if there is no discount
   *   taxInfo: {
   *     amount: number;
   *     code: string;
   *     taxGroup: string;
   *     totalTaxed: number;
   *     value: number;
   *   },
   * })) => JSX.element
   * ```
   */
  children: PropTypes.func,
  classes: PropTypes.shape({
    fullWidth: PropTypes.string,
    root: PropTypes.string
  }),
  className: PropTypes.string,
  currency: PropTypes.string.isRequired,
  // ISO kod meny (CZK, EUR...)
  decimalSignificance: PropTypes.number,
  discountAbsolute: PropTypes.number,
  discountPercent: PropTypes.number,
  fullWidth: PropTypes.bool,
  noTaxPrices: PropTypes.bool,
  price: PropTypes.number,
  priceOriginal: PropTypes.number,
  priceOriginalWithTax: PropTypes.number,
  priceWithTax: PropTypes.number,
  product: PropTypes.shape({
    id: PropTypes.string.isRequired,
    taxes: PropTypes.arrayOf(PropTypes.shape({
      region: PropTypes.string.isRequired,
      taxGroup: PropTypes.string.isRequired,
      value: PropTypes.number
    }))
  }),
  StandardComponentProsp: PropTypes.shape({
    priceLabelEnd: PropTypes.node,
    priceLabelStart: PropTypes.node
  }),
  taxInfo: PropTypes.shape({})
};
ProductPrice.defaultProps = {
  children: undefined,
  classes: undefined,
  className: undefined,
  decimalSignificance: 2,
  discountAbsolute: undefined,
  discountPercent: undefined,
  fullWidth: false,
  noTaxPrices: undefined,
  price: undefined,
  priceOriginal: undefined,
  priceOriginalWithTax: undefined,
  priceWithTax: undefined,
  product: undefined,
  StandardComponentProsp: undefined,
  taxInfo: undefined
};
export default ProductPrice;